import { IconPicture } from '@codexteam/icons';
import { make, linkCheck, isValidImageUrl, stopPropagationOnEditorJsControlKeys } from '@/editorjs/utils';

/**
 * Class for working with UI:
 *  - rendering base structure
 *  - show/hide preview
 *  - apply tune view
 */
export default class Ui {
  /**
   * @param {object} ui - image tool Ui module
   * @param {object} ui.api - Editor.js API
   * @param {ImageConfig} ui.config - user config
   * @param {Function} ui.onSelectFile - callback for clicks on Select file button
   * @param {boolean} ui.readOnly - read-only mode flag
   * @param {string} ui.loadedLink
   * @param {Function} ui.getOutOfLink
   */
  constructor({ api, config, onSelectFile, readOnly, loadedLink, getOutOfLink }) {
    this.createLinkArea = this.createLinkArea.bind(this);

    this.api = api;
    this.config = config;
    this.onSelectFile = onSelectFile;
    this.readOnly = readOnly;

    this.getOutofLink = getOutOfLink;

    this.linkState = {
      open: false,
      focused: false,
      data: loadedLink || '',
      stored: loadedLink || '',
      linkError: false,
      linkValid: false
    };

    this.toggleAddLink = this.toggleAddLink.bind(this);
    this.showCaptionAndLink = this.showCaptionAndLink.bind(this);
    this._handleClickOutside = this._handleClickOutside.bind(this); // [추가]

    this.nodes = {
      wrapper: make('div', [this.CSS.baseClass, this.CSS.wrapper]),
      imageContainer: make('div', [ this.CSS.imageContainer ]),
      fileButton: this.createFileButton(),
      imageEl: undefined,
      imagePreloader: make('div', this.CSS.imagePreloader),
      caption: make('div', [this.CSS.input, this.CSS.caption], {
        contentEditable: !this.readOnly,
      }),
      addLinkArea: this.createLinkArea(),
    };


    /**
     * Create base structure
     *  <wrapper>
     *    <image-container>
     *      <image-preloader />
     *    </image-container>
     *    <caption />
     *    <select-file-button />
     *  </wrapper>
     */
    this.nodes.caption.dataset.placeholder = this.config.captionPlaceholder;
    this.nodes.caption.addEventListener('input', () => {
      if (this.nodes.caption.innerHTML.trim() === '<br>') {
        this.nodes.caption.innerHTML = '';
      }
    });
    this.nodes.imageContainer.appendChild(this.nodes.imagePreloader);
    this.nodes.wrapper.appendChild(this.nodes.imageContainer);
    this.nodes.wrapper.appendChild(this.nodes.caption);
    this.nodes.wrapper.appendChild(this.nodes.fileButton);

    if (loadedLink) {
      this.linkState.open = true;
    }

    this.nodes.imageContainer.addEventListener('click', this.showCaptionAndLink);
    document.addEventListener('click', this._handleClickOutside);
  }

  destroy() {
    if (this._handleClickOutside) {
      document.removeEventListener('click', this._handleClickOutside);
    }
  }

  /**
   * Link state
   *
   * @returns {{data: string, focused: boolean, open: boolean}}
   * @class
   */
  get LinkState() {
    return this.linkState;
  }

  /**
   * set link validity
   * @param bool {boolean}
   * @constructor
   */
  set LinkError(bool) {
    this.linkState.linkError = bool;
    if (this.nodes.addLinkArea) {
      this.nodes.addLinkArea.classList.toggle(this.CSS.linkError, bool);
      // [추가] 에러 상태가 되면, 유효 상태는 해제합니다.
      if (bool) {
        this.LinkValid = false;
      }
    }
  }

  /**
   * set link valid state
   * @param bool {boolean}
   * @constructor
   */
  set LinkValid(bool) {
    this.linkState.linkValid = bool;
    if (this.nodes.addLinkArea) {
      this.nodes.addLinkArea.classList.toggle(this.CSS.linkValid, bool);
      // [추가] 유효 상태가 되면, 에러 상태는 해제합니다.
      if (bool) {
        this.LinkError = false;
      }
    }
  }

  /**
   * CSS classes
   *
   * @returns {object}
   */
  get CSS() {
    return {
      baseClass: this.api.styles.block,
      loading: this.api.styles.loader,
      input: this.api.styles.input,
      button: this.api.styles.button,

      /**
       * Tool's classes
       */
      wrapper: 'image-tool',
      imageContainer: 'image-tool__image',
      imagePreloader: 'image-tool__image-preloader',
      imageEl: 'image-tool__image-picture',
      caption: 'image-tool__caption',
      captionHidden: 'image-tool__caption--hidden',
      addLinkInput: 'image-tool__link-input',
      addLinkArea: 'image-tool__link-area',
      linkError: 'image-tool__link-error',
      linkValid: 'image-tool__link-valid',
    };
  };

  /**
   * Ui statuses:
   * - empty
   * - uploading
   * - filled
   *
   * @returns {{EMPTY: string, UPLOADING: string, FILLED: string}}
   */
  static get status() {
    return {
      EMPTY: 'empty',
      UPLOADING: 'loading',
      FILLED: 'filled',
    };
  }

  /**
   * Renders tool UI
   *
   * @param {ImageToolData} toolData - saved tool data
   * @returns {Element}
   */
  render(toolData) {
    const file = toolData.file;
    const caption = toolData.caption;

    if (!file || Object.keys(file).length === 0) {
      this.toggleStatus(Ui.status.EMPTY);
    } else if (file && file.url && !isValidImageUrl(file.url)) {
      this.toggleStatus(Ui.status.EMPTY);
    } else {
      this.toggleStatus(Ui.status.UPLOADING);
    }

    if (!caption) {
      this.nodes.caption.classList.add(this.CSS.captionHidden);
    }

    if (this.readOnly) {
      this.toggleStatus(Ui.status.FILLED);

      return this.nodes.wrapper;
    }

    if (this.linkState.open) {
      this.nodes.wrapper.prepend(this.nodes.addLinkArea);
      // [추가] 렌더링 시점에 유효성 검사를 하고 valid/error 상태를 적용
      if (this.linkState.data.trim() !== '') {
        if (this.validateLink()) {
          this.LinkValid = true;
        } else {
          this.LinkError = true;
        }
      }
    }

    return this.nodes.wrapper;
  }

  /**
   * Creates upload-file button
   *
   * @returns {Element}
   */
  createFileButton() {
    const button = make('div', [ this.CSS.button ]);

    button.innerHTML = this.config.buttonContent || `${IconPicture} ${this.api.i18n.t('Select an Image')}`;

    button.addEventListener('click', () => {
      this.onSelectFile();
    });

    return button;
  }

  /**
   * Shows uploading preloader
   *
   * @param {string} src - preview source
   * @returns {void}
   */
  showPreloader(src) {
    this.nodes.imagePreloader.style.backgroundImage = `url(${src})`;

    this.toggleStatus(Ui.status.UPLOADING);
  }

  /**
   * Hide uploading preloader
   *
   * @returns {void}
   */
  hidePreloader() {
    this.nodes.imagePreloader.style.backgroundImage = '';
    this.toggleStatus(Ui.status.EMPTY);
  }

  /**
   * Shows an image
   *
   * @param {string} url - image source
   * @returns {void}
   */
  fillImage(url) {
    /**
     * Check for a source extension to compose element correctly: video tag for mp4, img — for others
     */
    const tag = /\.mp4$/.test(url) ? 'VIDEO' : 'IMG';

    const attributes = {
      src: url,
    };

    /**
     * We use eventName variable because IMG and VIDEO tags have different event to be called on source load
     * - IMG: load
     * - VIDEO: loadeddata
     *
     * @type {string}
     */
    let eventName = 'load';

    /**
     * Update attributes and eventName if source is a mp4 video
     */
    if (tag === 'VIDEO') {
      /**
       * Add attributes for playing muted mp4 as a gif
       *
       * @type {boolean}
       */
      attributes.autoplay = true;
      attributes.loop = true;
      attributes.muted = true;
      attributes.playsinline = true;

      /**
       * Change event to be listened
       *
       * @type {string}
       */
      eventName = 'loadeddata';
    }

    /**
     * Compose tag with defined attributes
     *
     * @type {Element}
     */
    this.nodes.imageEl = make(tag, this.CSS.imageEl, attributes);

    /**
     * Add load event listener
     */
    this.nodes.imageEl.addEventListener(eventName, () => {
      this.toggleStatus(Ui.status.FILLED);

      /**
       * Preloader does not exists on first rendering with presaved data
       */
      if (this.nodes.imagePreloader) {
        this.nodes.imagePreloader.style.backgroundImage = '';
      }
    });

    this.nodes.imageContainer.appendChild(this.nodes.imageEl);
  }

  /**
   * Shows caption input
   *
   * @param {string} text - caption text
   * @returns {void}
   */
  fillCaption(text) {
    if (this.nodes.caption) {
      this.nodes.caption.innerHTML = text;
    }
  }

  /**
   * Changes UI status
   *
   * @param {string} status - see {@link Ui.status} constants
   * @returns {void}
   */
  toggleStatus(status) {
    for (const statusType in Ui.status) {
      if (Object.prototype.hasOwnProperty.call(Ui.status, statusType)) {
        this.nodes.wrapper.classList.toggle(`${this.CSS.wrapper}--${Ui.status[statusType]}`, status === Ui.status[statusType]);
      }
    }
  }

  /**
   * changes display link input status
   */
  toggleAddLink(open = !this.linkState.open) {
    this.linkState.open = open;

    if (open) {
      // [변경] 열릴 때: wrapper에 linkArea 노드를 추가하고 포커스를 줍니다.
      if (!this.nodes.wrapper.contains(this.nodes.addLinkArea)) {
        this.nodes.wrapper.prepend(this.nodes.addLinkArea);
      }

      this.LinkError = false;
      this.LinkError = false; // 열 때 항상 에러 상태 초기화
      // this.nodes.addLinkArea.querySelector('input').focus();
    } else {
      // [변경] 닫힐 때: linkArea 노드를 wrapper에서 제거합니다.
      if (this.nodes.wrapper.contains(this.nodes.addLinkArea)) {
        this.nodes.wrapper.removeChild(this.nodes.addLinkArea);
      }

      this.LinkError = false;
      this.LinkError = false; // 닫을 때 항상 에러 상태 초기화

      // [추가] 링크 값이 유효할 경우에만 stored에 저장
      const linkValue = this.linkState.data.trim();
      if (linkCheck(linkValue)) {
        this.linkState.stored = linkValue;
      } else if (linkValue === '') {
        // 빈 값이면 stored도 비워줌
        this.linkState.stored = '';
      }
    }
  }

  validateLink() {
    // [변경] 단순 유효성 검사 및 반환 기능으로 변경
    const linkValue = this.linkState.data.trim();
    // 빈 값은 유효한 것으로 간주 (링크를 설정하지 않은 상태)
    if (linkValue === '') {
      return true;
    }
    return linkCheck(linkValue);
  }

  onKeyDownInputLink = (event) => {
    stopPropagationOnEditorJsControlKeys(event)

    if (event.key === "Enter") {
      event.preventDefault(); // Enter키 기본 동작(줄바꿈 등) 방지
    }

    if (event.key === "Delete" || event.key === "Backspace") {
      this.LinkError = !this.validateLink();
    }
  }

  /**
   * creates inputArea
   * @returns {Element}
   */
  createLinkArea() {
    const linkArea = make('div', [this.CSS.addLinkArea, this.CSS.addLinkInput]);
    const linkInput = make('input', [this.CSS.addLinkInput, this.CSS.input], {
      placeholder: '링크 입력..',
    });

    linkInput.value = this.linkState.data;

    linkInput.addEventListener('focus', () => {
      this.linkState.focused = true;
      this.LinkError = false;
      this.LinkValid = false;
    });

    linkInput.addEventListener('blur', () => {
      this.linkState.focused = false;
    });

    linkInput.addEventListener('input', (event) => {
      this.linkState.data = event.target.value;
      this.linkState.stored = this.linkState.data;
      this.LinkError = !this.validateLink();
    });
    linkInput.addEventListener('keydown', this.onKeyDownInputLink);
    linkArea.appendChild(linkInput);

    return linkArea;
  }

  /**
   * Apply visual representation of activated tune
   *
   * @param {string} tuneName - one of available tunes {@link Tunes.tunes}
   * @param {any} status - true for enable, false for disable
   * @returns {void}
   */
  applyTune(tuneName, status) {
    this.nodes.wrapper.classList.toggle(`${this.CSS.wrapper}--${tuneName}`, status);
  }

  /**
   * [변경] 캡션과 링크 입력창을 함께 보여줍니다.
   * @private
   */
  showCaptionAndLink() {
    this.nodes.caption.classList.remove(this.CSS.captionHidden);

    // 링크 입력창이 닫혀있을 때만 열어줌
    if (!this.linkState.open) {
      this.toggleAddLink(true);
    }

    // 캡션에 포커스 (사용자가 캡션을 수정하려 할 수 있으므로)
    this.nodes.caption.focus({preventScroll: true});
  }


  /**
   * [추가] 문서의 다른 곳을 클릭했을 때 호출됩니다.
   * 캡션이 비어있으면 숨깁니다.
   * @param {MouseEvent} event - a click event
   * @private
   */
  _handleClickOutside(event) {
    // UI가 열려있지 않거나, 클릭된 위치가 이미지 툴 내부이면 아무것도 하지 않음
    if (!this.linkState.open || this.nodes.wrapper.contains(event.target)) {
      return;
    }

    // 캡션이 비어있으면 숨김
    if (this.nodes.caption.innerHTML.trim() === '' || this.nodes.caption.innerHTML.trim() === '<br>') {
      this.nodes.caption.classList.add(this.CSS.captionHidden);
    }

    const linkValue = this.linkState.data.trim();

    if (linkValue === '') {
      this.linkState.data = '';
      this.linkState.stored = '';
      this.toggleAddLink(false); // 링크 입력창 숨기기

      return;
    }

    // 2. 링크 값이 비어있지 않은 경우 -> 유효성 검사
    if (this.validateLink()) {
      // 2-1. 유효한 경우 -> valid 클래스 추가 (숨기지 않음)
      this.LinkValid = true;
    } else {
      // 2-2. 유효하지 않은 경우 -> error 클래스 추가 (숨기지 않음)
      this.LinkError = true;
    }
  }
}